import { useState, useEffect } from 'react';

export const usePWA = () => {
    const [isInstallable, setIsInstallable] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isOnline, setIsOnline] = useState(navigator.onLine);

    useEffect(() => {
        // Check if app is already installed
        const checkInstalled = () => {
            const mediaQueryResult = typeof window.matchMedia === 'function'
                ? window.matchMedia('(display-mode: standalone)')
                : null;
            const isStandalone = !!(mediaQueryResult && mediaQueryResult.matches);
            const isInWebAppiOS = window.navigator.standalone === true;
            setIsInstalled(isStandalone || isInWebAppiOS);
        };

        checkInstalled();

        // Listen for install prompt
        const handleBeforeInstallPrompt = (e) => {
            console.log('[PWA] Before install prompt fired');
            e.preventDefault();
            setDeferredPrompt(e);
            // Only show install prompt if not previously dismissed
            const dismissed = localStorage.getItem('pwa-install-prompt-dismissed') === 'true';
            if (!dismissed) {
                setIsInstallable(true);
            }
        };

        // Listen for successful installation
        const handleAppInstalled = () => {
            console.log('[PWA] App installed');
            setIsInstalled(true);
            setIsInstallable(false);
            setDeferredPrompt(null);
        };

        // Listen for online/offline status
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const installPWA = async () => {
        if (!deferredPrompt) {
            console.log('[PWA] No install prompt available');
            return false;
        }

        try {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;

            if (outcome === 'accepted') {
                console.log('[PWA] User accepted install');
                setIsInstalled(true);
            } else {
                console.log('[PWA] User dismissed install');
            }

            setDeferredPrompt(null);
            setIsInstallable(false);
            return outcome === 'accepted';
        } catch (error) {
            console.error('[PWA] Install failed:', error);
            return false;
        }
    };

    const dismissInstallPrompt = () => {
        console.log('[PWA] User dismissed install prompt');
        setIsInstallable(false);
        localStorage.setItem('pwa-install-prompt-dismissed', 'true');
    };

    const shareContent = async (data) => {
        if (!navigator.share) {
            console.log('[PWA] Web Share API not supported');
            return false;
        }

        try {
            await navigator.share(data);
            return true;
        } catch (error) {
            console.error('[PWA] Share failed:', error);
            return false;
        }
    };

    const requestNotificationPermission = async () => {
        if (!('Notification' in window)) {
            console.log('[PWA] Notifications not supported');
            return false;
        }

        if (Notification.permission === 'granted') {
            return true;
        }

        if (Notification.permission === 'denied') {
            return false;
        }

        try {
            const permission = await Notification.requestPermission();
            return permission === 'granted';
        } catch (error) {
            console.error('[PWA] Notification permission request failed:', error);
            return false;
        }
    };

    const vibrate = (pattern) => {
        if (!navigator.vibrate) {
            console.log('[PWA] Vibration not supported');
            return false;
        }

        navigator.vibrate(pattern);
        return true;
    };

    // Send local notification
    const sendNotification = async (title, options = {}) => {
        if (Notification.permission !== 'granted') {
            console.log('[PWA] Notification permission not granted');
            return null;
        }

        try {
            const notification = new Notification(title, {
                icon: '/logo192.png',
                badge: '/logo192.png',
                ...options
            });

            // Auto-close after 5 seconds if not specified
            if (!options.requireInteraction) {
                setTimeout(() => {
                    notification.close();
                }, 5000);
            }

            return notification;
        } catch (error) {
            console.error('[PWA] Send notification failed:', error);
            return null;
        }
    };

    // Check if running in standalone mode (installed PWA)
    const isStandalone = () => {
        const mediaQueryResult = typeof window.matchMedia === 'function'
            ? window.matchMedia('(display-mode: standalone)')
            : null;

        return !!(mediaQueryResult && mediaQueryResult.matches) ||
            window.navigator.standalone === true;
    };

    // Get device orientation
    const getDeviceOrientation = () => {
        if (window.screen && window.screen.orientation) {
            return window.screen.orientation.type;
        }
        return 'unknown';
    };

    // Request device orientation permission
    const requestOrientationPermission = async () => {
        if (!window.screen || !window.screen.orientation || !window.screen.orientation.lock) {
            console.log('[PWA] Screen orientation lock not supported');
            return false;
        }

        try {
            await window.screen.orientation.lock('portrait-primary');
            return true;
        } catch (error) {
            console.error('[PWA] Orientation lock failed:', error);
            return false;
        }
    };

    // Check if device supports touch
    const isTouchDevice = () => {
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    };

    // Get battery status
    const getBatteryStatus = async () => {
        if (!navigator.getBattery) {
            console.log('[PWA] Battery API not supported');
            return null;
        }

        try {
            const battery = await navigator.getBattery();
            return {
                charging: battery.charging,
                chargingTime: battery.chargingTime,
                dischargingTime: battery.dischargingTime,
                level: battery.level * 100
            };
        } catch (error) {
            console.error('[PWA] Battery status failed:', error);
            return null;
        }
    };

    return {
        isInstallable,
        isInstalled,
        isOnline,
        installPWA,
        dismissInstallPrompt,
        shareContent,
        requestNotificationPermission,
        vibrate,
        sendNotification,
        isStandalone,
        getDeviceOrientation,
        requestOrientationPermission,
        isTouchDevice,
        getBatteryStatus
    };
};


